import React, { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import translations from "../utils/data";


const renderContentToHtml = (content, featuredImage) => {
  // Remove script tags for safety
  const sanitized = content.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

  // Split content into sections based on '##'
  const sections = sanitized.split(/(?=##\s+)/);

  const cleanHashMarks = (text) => {
    return text
      .replace(/^\s*#\s*$/gm, '')         // Remove lines that contain only #
      .replace(/\s*#\s*$/gm, '')          // Remove trailing # at end of line
      .replace(/#(?=\s|$)/gm, '')         // Remove standalone # anywhere
      .replace(/#(?=<)/gm, '')            // Remove # before HTML tags
      .replace(/\(para align\)/gi, '')    // Remove custom (para align)
      .trim();
  };

  const processedSections = sections.map((section, index) => {
    if (section.startsWith('##')) {
      const [titleLine, ...contentLines] = section.split('\n');
      const title = cleanHashMarks(titleLine.replace(/^##\s*/, '').trim());
      const content = cleanHashMarks(contentLines.join('\n'))
        .replace(/\[([^\]]+)\]/g, '<strong class="text-[#9f7a2f]">$1</strong>')
        .replace(/###\s*(.+)/g, '<h3 class="text-xl font-semibold mb-3">$1</h3>')
        .replace(/\n\n+/g, '</p><p>')
        .replace(/\n/g, '<br/>');

      if (index === 0) {
        return `
          <div class="prose prose-lg max-w-none mb-12">
            <p class="text-gray-700 leading-relaxed">${content}</p>
          </div>
        `;
      } else if (index === 1) {
        return `
          <div class="flex flex-col md:flex-row items-start gap-8 my-12">
            <div class="w-full md:w-1/2">
              <h2 class="text-2xl font-bold mb-4 text-gray-800 border-b-2 border-[#9f7a2f33] pb-3">${title}</h2>
              <div class="prose prose-lg prose-p:mb-4 prose-strong:text-[#9f7a2f]">
                <p>${content}</p>
              </div>
            </div>
            <div class="w-full md:w-1/2 rounded-xl overflow-hidden">
              <img 
                src="${featuredImage}" 
                alt="${title}"
                class="w-full h-full object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>
        `;
      } else {
        return `
          <div class="my-12">
            <h2 class="text-2xl font-bold mb-6 text-gray-800 border-b-2 border-[#9f7a2f33] pb-3">${title}</h2>
            <div class="prose prose-lg prose-p:mb-4 prose-strong:text-[#9f7a2f]">
              <p>${content}</p>
            </div>
          </div>
        `;
      }
    } else {
      const processedContent = cleanHashMarks(section)
        .replace(/\[([^\]]+)\]/g, '<strong class="text-[#9f7a2f]">$1</strong>')
        .replace(/###\s+(.+)/g, '<h3 class="text-xl font-semibold mt-6 mb-3 pl-4 border-l-4 border-[#9f7a2f]">$1</h3>')
        .replace(/\n\n+/g, '</p><p class="mb-4">')
        .replace(/\n/g, '<br/>');

      return processedContent
        ? `<div class="prose prose-lg max-w-none my-6"><p class="text-gray-700 leading-relaxed">${processedContent}</p></div>`
        : '';
    }
  });

  return processedSections.join('');
};


const BlogPost = () => {
  const { slug } = useParams();
  const post = useMemo(() => translations.blogs.find((b) => b.slug === slug), [slug]);
  const html = useMemo(() => (post ? renderContentToHtml(post.content, post.image) : ""), [post]);

  useEffect(() => {
    if (!post) {
      document.title = "Blog Post Not Found";
      return;
    }

    document.title = `${post.title} | Sacred Relm`;

    // Meta description handling
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", post.metaDescription || "");
  }, [post]);

  if (!post) {
    return (
      <main className="max-w-[860px] mx-auto p-4">
        <nav className="mb-4">
          <Link
            to="/blog"
            className="text-[#9f7a2f] no-underline"
          >
            ← Back to Blogs
          </Link>
        </nav>
        <h1 className="text-2xl font-bold">Blog Post Not Found</h1>
        <p>Sorry, the blog post you're looking for doesn't exist.</p>
      </main>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-4 md:px-6 py-12">
      <nav className="mb-8 flex items-center">
        <Link
          to="/blog"
          className="text-[#9f7a2f] no-underline flex items-center gap-2 text-[1.1em] font-medium px-4 py-2 rounded-lg bg-[#9f7a2f11] transition-all duration-200 hover:bg-[#9f7a2f22]"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Blogs
        </Link>
      </nav>

      <header className="text-center mb-12 p-8 bg-gradient-to-br from-[#9f7a2f11] to-[#9f7a2f05] rounded-2xl">
        <h1 className="text-4xl md:text-5xl mb-4 text-[#1a1a1a] leading-snug">
          {post.title}
        </h1>
        <div className="flex items-center justify-center gap-4 text-[#666] text-[1.1em]">
          <span className="px-4 py-1.5 bg-[#9f7a2f11] rounded-xl text-[#9f7a2f]">
            {post.date}
          </span>
          <span>•</span>
          <span>{post.readTime}</span>
        </div>
      </header>

      <article className="leading-7 text-[1.1em] text-[#2a2a2a]">
        <div dangerouslySetInnerHTML={{ __html: html }} />
      </article>

      {/* {post.tags?.length ? (
        <footer className="mt-12 p-8 bg-[#f8f8f8] rounded-xl">
          <div className="flex gap-3 flex-wrap items-center">
            <span className="text-[#666] text-[1.1em] mr-2">Topics:</span>
            {post.tags.map((t) => (
              <span
                key={t}
                className="text-[0.95em] px-4 py-1.5 rounded-full bg-white text-[#9f7a2f] shadow-sm border border-[#9f7a2f33]"
              >
                #{t}
              </span>
            ))}
          </div>
        </footer>
      ) : null} */}
    </main>
  );
};

export default BlogPost;
